import { BlockStack, InlineStack, Text, Divider } from '@shopify/polaris'
import type { DraftOrder } from '../lib/graphql'
import { currencySymbol } from '../lib/currency'

type Props = { draftOrder: DraftOrder }

function SummaryRow({ label, middle, amount, bold }: { label: string; middle?: string; amount: string; bold?: boolean }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', alignItems: 'baseline', gap: '8px' }}>
            <Text as="span" fontWeight={bold ? 'bold' : undefined} tone={bold ? undefined : 'subdued'}>{label}</Text>
            <Text as="span" tone="subdued">{middle || ''}</Text>
            <Text as="span" fontWeight={bold ? 'bold' : undefined} alignment="end">{amount}</Text>
        </div>
    )
}

export function OrderSummary({ draftOrder }: Props) {
    const total = draftOrder.totalPriceSet.shopMoney
    const tax = draftOrder.totalTaxSet.shopMoney
    const discount = draftOrder.totalDiscountsSet?.shopMoney
    const shipping = draftOrder.totalShippingPriceSet?.shopMoney
    const symbol = currencySymbol(total.currencyCode)
    const itemCount = draftOrder.lineItems.nodes.reduce((sum, i) => sum + i.quantity, 0)

    // Subtotal = sum of line items (before discount/shipping)
    const lineItemsTotal = draftOrder.lineItems.nodes.reduce(
        (sum, i) => sum + parseFloat(i.originalUnitPriceSet.shopMoney.amount) * i.quantity, 0
    )
    const discountAmount = discount ? parseFloat(discount.amount) : 0
    const shippingAmount = shipping ? parseFloat(shipping.amount) : 0

    return (
        <BlockStack gap="300">
            {draftOrder.lineItems.nodes.map((item) => (
                <InlineStack key={item.id} align="space-between">
                    <Text as="span">{item.title} &times; {item.quantity}</Text>
                    <Text as="span">
                        {symbol}{(parseFloat(item.originalUnitPriceSet.shopMoney.amount) * item.quantity).toFixed(2)}
                    </Text>
                </InlineStack>
            ))}
            <Divider />
            <SummaryRow
                label="Subtotal"
                middle={`${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
                amount={`${symbol}${lineItemsTotal.toFixed(2)}`}
            />
            {discountAmount > 0 && (
                <SummaryRow
                    label="Discount"
                    middle={draftOrder.appliedDiscount?.title || ''}
                    amount={`-${symbol}${discountAmount.toFixed(2)}`}
                />
            )}
            {shippingAmount > 0 && (
                <SummaryRow
                    label="Shipping"
                    middle={draftOrder.shippingLine?.title || ''}
                    amount={`${symbol}${shippingAmount.toFixed(2)}`}
                />
            )}
            <SummaryRow
                label="Estimated tax"
                middle={draftOrder.taxExempt ? 'Not collected' : (parseFloat(tax.amount) > 0 ? '' : 'Not calculated')}
                amount={!draftOrder.taxExempt && parseFloat(tax.amount) > 0 ? `${symbol}${parseFloat(tax.amount).toFixed(2)}` : ''}
            />
            <Divider />
            <SummaryRow label="Total" amount={`${symbol}${parseFloat(total.amount).toFixed(2)}`} bold />
        </BlockStack>
    )
}
